import { Loading } from '@/components/loading/Loading';
import SiTrendingUpFill from '@/icons/SiTrendingUpFill';
import { getModelTypeString, getPeripheralModelTypeString } from '@/pages/settings/bulk/helpers/utils';
import {
  FinanceModel,
  FinanceModelTypes,
  PeripheralFinanceModel,
  PeripheralFinanceModelTypes
} from '@/services/api/contracts/financemodels';
import { useCategory } from '@/services/api/hooks/catalog';
import { useProduct, useProductPriceHistory } from '@/services/api/hooks/product';
import { RoleType } from '@/services/authentication/hooks/user';
import { useCatalogueService } from '@/services/catalogue/useCatalogueService';
import { useDeviceFee } from '@/services/catalogue/useDeviceFee';
import { useSelectedFinanceModelForCategoryAndManufacturer } from '@/services/financemodel/useSelectedFinanceModelForCategoryAndManufacturer';
import { usePoDeviceFee } from '@/services/purchaseOrders/usePurchaseOrderService';
import { toMajor, usePrintCurrencyString } from '@/utils/price';
import { FC, Suspense, useMemo } from 'react';
import LocalizedStrings from 'react-localization';
import { Modal } from '../../../components/modal/Modal';
import { ModalProps } from '../../../components/modal/useModal';
import { Card } from '../../../components/sections/Card';
import { DEVICE_FEE_CATEGORY_ID } from '../consts';
import CataloguePCMField from './CataloguePCMField';
import { getBreakdown } from './priceHelper';

const strings = new LocalizedStrings({
  en: {
    base: 'Base',
    costHistory: 'Cost history',
    daasPortal:
      "DaaS Portal keeps a timeline of cost history to ensure you have the most accurate and clear representation of a product's cost.",
    current: 'Current',
    upcoming: 'Upcoming'
  }
});

interface PriceBreakdown {
  price: number;
  date: Date;
  percentages: { party: string; margin: number; contribution: number }[];
  categoryId: string;
  manufacturerId: string;
  regionCode?: string;
  showDeviceFee?: boolean;
  showFullDp?: boolean;
}

const PriceBreakDownPCMCost: FC<PriceBreakdown> = ({ categoryId, manufacturerId, price, regionCode }) => {
  const [category] = useCategory(categoryId);

  const model = useSelectedFinanceModelForCategoryAndManufacturer(categoryId, manufacturerId);
  const modelType = useMemo(() => {
    if (!model) return 'Capital Purchase';

    const type = category.isMainDevice
      ? (model as FinanceModel).financeModelType
      : (model as PeripheralFinanceModel).peripheralFinanceModelType;

    return category.isMainDevice
      ? getModelTypeString(type as unknown as FinanceModelTypes)
      : getPeripheralModelTypeString(type as unknown as PeripheralFinanceModelTypes);
  }, [model]);

  return (
    <>
      <PriceBreakDownCost
        price={price}
        pcm={categoryId === DEVICE_FEE_CATEGORY_ID}
        model={model || undefined}
        regionCode={regionCode}
      />
      <p className="text-md font-semibold">{modelType}</p>
    </>
  );
};

const PriceBreakDownCost: FC<{
  price: number;
  pcm?: boolean;
  model?: FinanceModel | PeripheralFinanceModel;
  regionCode?: string;
}> = ({ price, pcm, model, regionCode }) => {
  const [configuration] = useCatalogueService(state => [state.configuration]);
  const printCurrencyString = usePrintCurrencyString({
    regionCode,
    forceSpecifyRegion: configuration.as === RoleType.Admin
  });
  const printedPrice = printCurrencyString(price);

  return (
    <div className="flex items-center gap-3">
      <p className="text-xl font-semibold tracking-tight flex items-center gap-2">
        <span>{printedPrice}</span>
        {pcm && 'pcm'}
      </p>
      {model && model.pcmFactor && <CataloguePCMField model={model} price={price} regionCode={regionCode} />}
    </div>
  );
};

export const PriceBreakdown: FC<PriceBreakdown> = ({
  price,
  percentages,
  categoryId,
  manufacturerId,
  date,
  regionCode,
  showDeviceFee,
  showFullDp
}) => {
  const base = getBreakdown(price, percentages);
  const [configuration, inPurchaseOrder] = useCatalogueService(state => [state.configuration, state.inPurchaseOrder]);

  const canShowPCM = categoryId !== DEVICE_FEE_CATEGORY_ID && configuration.for === RoleType.Company;
  const printCurrencyString = usePrintCurrencyString({
    regionCode,
    forceSpecifyRegion: configuration.as === RoleType.Admin
  });

  const defaultDeviceFee = useDeviceFee();
  const poDeviceFee = usePoDeviceFee();

  return (
    <div className="mb-2 flex flex-col group-last-of-type:mb-0">
      {canShowPCM ? (
        <PriceBreakDownPCMCost
          date={date}
          price={price}
          percentages={percentages}
          categoryId={categoryId}
          manufacturerId={manufacturerId}
          regionCode={regionCode}
        />
      ) : (
        <PriceBreakDownCost price={price} regionCode={regionCode} />
      )}
      {showDeviceFee && defaultDeviceFee && (
        <div>{`Device Fee: ${printCurrencyString(inPurchaseOrder ? poDeviceFee : defaultDeviceFee)}`}</div>
      )}
      {percentages.length !== 0 && (
        <div className="flex items-center gap-1">
          <span className="text-primary-std text-sm font-semibold">{printCurrencyString(base)}</span>
          <span className="text-xs opacity-50">•</span>
          <span className="text-secondary-std text-sm font-medium">{strings.base}</span>
        </div>
      )}
      {percentages.map(({ party, margin, contribution }) => {
        if (!contribution) return null;

        let partyValue = party;
        if (partyValue === 'MspBase') {
          partyValue = 'MSP Discount';
        }

        return (
          <div
            key={`${categoryId}-${manufacturerId}-${party}-${margin}-${contribution}`}
            className="flex items-center gap-1"
          >
            <span className="text-primary-std text-sm font-semibold">{printCurrencyString(contribution)}</span>
            <span className="text-xs opacity-50">•</span>
            <span className="text-secondary-std text-sm font-medium">{partyValue}</span>
            <span className="text-xs opacity-50">•</span>
            <span className="text-secondary-std text-sm font-medium">
              {toMajor(margin).toLocaleString('en-GB', {
                style: 'percent',
                minimumFractionDigits: 1,
                maximumFractionDigits: showFullDp ? 8 : 2
              })}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const PriceHistory: FC<{ productId: string; showFullDp?: boolean }> = ({ productId, showFullDp }) => {
  const [history] = useProductPriceHistory(productId);
  const [product] = useProduct(productId);

  return (
    <div className="mt-3 flex flex-col">
      {history.map(({ date: d, price, margins }) => {
        const date = new Date(d);
        return (
          <div className="group flex w-full justify-between" key={`${productId}-${price}-${date.toISOString()}`}>
            <div className="flex gap-2">
              <div className="relative w-4">
                <span className="absolute top-1.5 left-0 z-10 h-4 w-4 rounded-full bg-[#A9D6DD]"></span>
                <span className="bg-border absolute top-1.5 bottom-0 left-1/2 h-full w-0.5 -translate-x-1/2 group-last:hidden"></span>
              </div>
              <PriceBreakdown
                date={date}
                price={price}
                percentages={margins}
                categoryId={product.category.id}
                manufacturerId={product.manufacturer.id}
                regionCode={product.region?.code}
                showFullDp={showFullDp}
              />
            </div>
            <span className="text-secondary-std text-sm font-medium">{date.toLocaleDateString()}</span>
          </div>
        );
      })}
    </div>
  );
};

export const PriceHistoryModal: FC<ModalProps<{ productId: string }>> = props => {
  return (
    <Modal className="md:max-w-xl" {...props}>
      <Card className="flex w-full flex-col gap-2 p-10 pb-12">
        <Modal.Controls {...props}>
          <h1 className="flex gap-2 text-2xl font-semibold">
            <span className="bg-primary-std rounded-full p-1 text-2xl">
              <SiTrendingUpFill />
            </span>
            <span>{strings.costHistory}</span>
          </h1>
        </Modal.Controls>
        <p className="leading-tight">{strings.daasPortal}</p>
        {props && props.props && (
          <Suspense fallback={<Loading />}>
            <PriceHistory productId={props.props.productId} showFullDp={true} />
          </Suspense>
        )}
      </Card>
    </Modal>
  );
};
